import React, { useCallback, useMemo, useState } from 'react';
import { DialogActions, DialogContent, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { AppRow } from '../../layout/appRow';
import { AutocompleteOption } from '../../models/autocompleteOption';
import { Project } from '../../models/project';
import { Task, taskStatusOptions } from '../../models/task';
import { AppButton } from '../app/button/appButton';
import { AppControlledAutocomplete } from '../app/input/appControlledAutocomplete';
import { AppControlledCheckbox } from '../app/input/appControlledCheckbox';
import { AppControlledSelect } from '../app/input/appControlledSelect';
import { NO_PROJECTS, PLEASE_TYPE } from '../../constants';
import { useGetProjects } from '../../hooks/useGetProjects';
import { AppControlledTextField } from '../app/input/appControlledTextField';
import { generateProjectLabel } from '../../services/projectService';

const style = {
  dialogActions: {
    p: 0,
    mt: 2,
  },
} as const;

interface TaskFormProp {
  onSubmit(data: Partial<Task>): void;
  task?: Task | null;
  onClose(): void;
  project?: Project | null;
  isLoading?: boolean;
  readOnly?: boolean;
}

interface TaskFormValues {
  project: AutocompleteOption | null;
  name: string;
  status: string;
  isAMS: boolean;
}

export function TaskForm({
  onSubmit,
  task,
  onClose,
  project,
  isLoading,
  readOnly = false,
}: TaskFormProp): JSX.Element {
  const defaultValues = {
    project: project
      ? {
          id: project.id,
          label: generateProjectLabel(project.name, project.code),
        }
      : null,
    name: task?.name,
    status: task?.status,
    isAMS: task?.isAMS,
  };

  const [projectFilter, setProjectFilter] = useState('');
  const [isFieldUntyped, setIsFieldUntyped] = useState(true);

  const methods = useForm<TaskFormValues>({ mode: 'all', defaultValues });

  const { projects, isLoading: isLoadingProjects } = useGetProjects(
    projectFilter,
    isFieldUntyped
  );

  const projectOptions = useMemo<AutocompleteOption[]>(() => {
    return projects.map((projectItem) => ({
      id: projectItem.id,
      label: generateProjectLabel(projectItem.name, projectItem.code),
    }));
  }, [projects]);

  const submit = useCallback(
    (data) => {
      onSubmit(data);
    },
    [onSubmit]
  );

  const activeUsers = task?.employees.filter(
    (employee) => employee.isActiveOnTask
  );

  const projectField = methods.watch('project');

  const isChangeProject = useMemo(
    () =>
      task?.projectId &&
      projectField?.id &&
      projectField?.id !== task?.projectId,
    [projectField, task]
  );

  const isDirty = methods.formState.isDirty;

  const getProjectsByFilter = useCallback(
    (filter: string) => {
      setProjectFilter(filter);
      if (isFieldUntyped) {
        setIsFieldUntyped(false);
      }
    },
    [isFieldUntyped]
  );

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(submit)}
        noValidate
        autoComplete="off"
      >
        <DialogContent>
          <AppControlledAutocomplete
            label="Project"
            name="project"
            options={projectOptions || []}
            sx={{ mb: 3 }}
            isLoading={isLoadingProjects}
            searchOptions={getProjectsByFilter}
            noOptionsText={NO_PROJECTS}
            readOnly={readOnly}
            placeholder={PLEASE_TYPE}
            isError={!!methods.formState.errors.project}
            isRequired
          />

          {isChangeProject && (
            <Typography>Task will be moved to this project</Typography>
          )}

          <AppControlledTextField
            name="name"
            label="Task Name"
            sx={{ mb: 3, mt: 1 }}
            isError={!!methods.formState.errors.name}
            isRequired
            fullWidth
          />

          <AppControlledSelect
            name="status"
            label="Status"
            options={taskStatusOptions}
            sx={{ mb: 3, mt: 1 }}
          />
          
          <AppControlledCheckbox 
            label="Apply AMS settings" 
            name="isAMS" 
            labelSx={{ marginTop: 1 }}
          />

          <Typography>Users added: {activeUsers?.length}</Typography>
        </DialogContent>
        <DialogActions sx={style.dialogActions}>
          <AppButton label="Cancel" onClick={onClose} />
          <AppButton
            label="Save"
            disabled={!isDirty}
            isPrimary
            buttonType="submit"
            isLoading={isLoading}
          />
        </DialogActions>
      </form>
    </FormProvider>
  );
}
